import React, { useCallback, useMemo } from 'react'
import Select, { SingleValue } from 'react-select'
import { t } from 'src/translations'

import {
  CatalogProductOption,
  CatalogProductOptionValue
} from 'src/apollo/onlineOrdering'
import { Product } from '../../hooks/useProductVariants'
import { ModifierGroupWrapper } from './ModifierGroupWrapper'

export type SelectedOption = {
  optionId: string
  valueId: string
}

export type VariationOptionType = {
  value: CatalogProductOptionValue
  label: string
}

type VariationModifiersSectionProps = {
  product: Product
  selectedOptions: SelectedOption[]
  onChange: (
    selection: SingleValue<VariationOptionType>,
    optionId: string
  ) => void
  variationOptionsInvalid: boolean
  unselectedOptions: CatalogProductOption[]
}

export const VariationModifiersSection = ({
  product,
  selectedOptions,
  onChange,
  variationOptionsInvalid,
  unselectedOptions
}: VariationModifiersSectionProps) => {
  // Sort options by sortOrder
  const sortedOptions = useMemo(() => {
    return [...product.availableOptions].sort(
      (a, b) => a.sortOrder - b.sortOrder
    )
  }, [product.availableOptions])

  return (
    <div data-testid='variationOptionSets'>
      {sortedOptions.map((option) => {
        const isInvalid =
          variationOptionsInvalid &&
          unselectedOptions.some((unselected) => unselected.id === option.id)
        return (
          <VariationOptionSetSelect
            key={option.id}
            option={option}
            product={product}
            selectedOptions={selectedOptions}
            onChange={(selection) => onChange(selection, option.id)}
            isInvalid={isInvalid}
          />
        )
      })}
    </div>
  )
}

const VariationOptionSetSelect = ({
  option,
  product,
  selectedOptions,
  onChange,
  isInvalid = false
}: {
  option: CatalogProductOption
  product: Product
  selectedOptions: SelectedOption[]
  onChange: (selection: SingleValue<VariationOptionType>) => void
  isInvalid?: boolean
}) => {
  // Determine which values are available based on other selections
  const availableValueIds = useMemo(() => {
    // Get selections for other options (not this one)
    const otherSelections = selectedOptions.filter(
      (sel) => sel.optionId !== option.id
    )

    // Find variants that match other selections
    const matchingVariants = product.variants.filter((variant) => {
      // If no other selections, all variants match
      if (otherSelections.length === 0) {
        return true
      }
      // Otherwise, check if variant matches all other selections
      return otherSelections.every((selection) =>
        variant.catalogProductInfo?.productVariant?.selectedOptions?.some(
          (opt) =>
            opt.id === selection.optionId && opt.value.id === selection.valueId
        )
      )
    })

    // Collect all value IDs for this option from matching variants
    const availableIds = new Set<string>()
    matchingVariants.forEach((variant) => {
      const variantOption =
        variant.catalogProductInfo?.productVariant?.selectedOptions?.find(
          (opt) => opt.id === option.id
        )
      if (variantOption) {
        availableIds.add(variantOption.value.id)
      }
    })

    return availableIds
  }, [option, product.variants, selectedOptions])

  // Sort option values by sortOrder and map to dropdown options
  const options: VariationOptionType[] = useMemo(() => {
    return [...option.values]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((value) => ({
        value: value,
        label: value.name,
        isDisabled: !availableValueIds.has(value.id)
      }))
  }, [option.values, availableValueIds])

  // Find currently selected value for this option
  const currentSelection = selectedOptions.find(
    (sel) => sel.optionId === option.id
  )
  const selectedOption = currentSelection
    ? options.find((opt) => opt.value.id === currentSelection.valueId) || null
    : null

  // Check if this option is valid (has a selection)
  const isOptionValid = useCallback(
    (optionId: string) => {
      return selectedOptions.some((sel) => sel.optionId === optionId)
    },
    [selectedOptions]
  )

  const ariaLabel = t('modifiers-section.option-set-aria-label', {
    optionName: option.name
  })

  return (
    <ModifierGroupWrapper
      guid={option.id}
      name={option.name}
      ariaLabel={ariaLabel}
      required
      maxSelections={1}
      minSelections={1}
      isInvalid={isInvalid}
      isModifierValid={isOptionValid}
    >
      <Select
        inputId={option.id}
        aria-labelledby={ariaLabel}
        instanceId={option.id}
        classNamePrefix='react-select'
        isClearable={false}
        isSearchable={false}
        required
        menuPosition='fixed'
        value={selectedOption}
        options={options}
        onChange={onChange}
        placeholder={t('modifiers-section.select-option', {
          optionName: option.name
        })}
      />
    </ModifierGroupWrapper>
  )
}
